import AccountFormulaNode from "../../../keyFigure/formula/AccountFormulaNode.js";
import AdditionFormulaNode from "../../../keyFigure/formula/AdditionFormulaNode.js";
import AndSpecification from "../../../specification/AndSpecification.js";
import GroupRow from "./GroupRow.js";
import NotSpecification from "../../../specification/NotSpecification.js";
import UndefinedAccountGroup from "../../../chartOfAccounts/UndefinedAccountGroup.js";

/**
 * Abstract row used for Chart of accounts account groups. The
 * `AccountGroupRow` subclass is for user-specified rows and the
 * `GeneratedAccountGroupRow` subclass for automatically generated
 * ones based on a given chart of accounts and existing transactions.
 *
 * @param {accountGroup} spec.accountGroup - CoA Account group of the row
 * @param {accountGroup[]} [spec.excludedAccountGroups=[]] - Account
 * groups whose value should not be part of the receiver's own
 * value
 */
export default class AbstractAccountGroupRow extends GroupRow {
	constructor({ accountGroup, excludedAccountGroups = [] }) {
		super(...arguments);
		this._accountGroup = accountGroup;
		this._excludedAccountGroups = excludedAccountGroups;
	}

	getAccountGroup() {
		return this._accountGroup;
	}

	isAccountGroupRow() {
		return true;
	}

	asSpecification() {
		const accountSpecification = this._accountGroup.asSpecification(
			this.getAccountOperation(),
		);

		if (this._excludedAccountGroups.length === 0) {
			return accountSpecification;
		}

		const excludedAccountGroupSpecifications =
			this._excludedAccountGroups.map(
				(excludedAccountGroup) =>
					new NotSpecification({
						specification: excludedAccountGroup.asSpecification(),
					}),
			);

		return new AndSpecification({
			specifications: [
				accountSpecification,
				...excludedAccountGroupSpecifications,
			],
		});
	}

	accept(visitor) {
		return visitor.visitAccountGroupRow(this);
	}

	_asBaseFormulaNode() {
		const accountGroupFormula = this._makeAccountGroupNode();

		// Some rows have manually-injected child rows (e.g., the
		// balance sheet contains injected rows for "Retained
		// earnings" and "Net profit"). These rows and all their
		// account group parent rows must reference the
		// manually-injected rows in their formula.
		const extraRows = this.getAllRows().filter(
			(row) => !row.isGenerated() && row.canBeCalculated(),
		);

		if (extraRows.length === 0) {
			return accountGroupFormula;
		}

		const extraRowsFormula = this._rowsAsFormulaNode(extraRows);

		return new AdditionFormulaNode({
			leftOperand: extraRowsFormula,
			rightOperand: accountGroupFormula,
		});
	}

	_makeAccountGroupNode() {
		const accountSpecification = this.asSpecification();

		const accountGroupNode = new AccountFormulaNode({
			accountSpecification,
		});

		// Only leaves are negated. The account group node is a leaf added
		// when making the formula node, so negate if needed.
		if (this.isNegated()) {
			return this._negatedFormulaNode(accountGroupNode);
		}

		return accountGroupNode;
	}

	_serializedProperties() {
		const properties = super._serializedProperties(...arguments);

		properties.accountGroupId = this._accountGroup.getId();

		properties.excludedAccountGroupIds = this._excludedAccountGroups.map(
			(group) => group.getId(),
		);

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		if (data.accountGroupId) {
			data.accountGroup =
				context.chartOfAccounts?.findById(data.accountGroupId) ||
				new UndefinedAccountGroup({
					id: data.accountGroupId,
					chartOfAccounts: context.chartOfAccounts,
				});
		}
	}
}
